import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Camera, ChatCircleText, Microphone, ChartLineUp, FunnelSimple, GearSix, GitBranch, Stack,
  Cpu, Eye, Robot, Lightning,
} from "@phosphor-icons/react";
import { api } from "../lib/api";

// Icon name (stored in the CMS atlas record) → component.
// Defaults to <Stack/> if the name is unknown so editors can never break the UI.
const ICONS = {
  Camera, ChatCircleText, Microphone, ChartLineUp, FunnelSimple, GearSix, GitBranch, Stack,
  Cpu, Eye, Robot, Lightning,
};

const SPAN_FALLBACK_FOR_INDEX = (i) => {
  if (i === 0) return "md:col-span-2 md:row-span-2";
  if (i === 1 || i === 4 || i === 7) return "md:col-span-2";
  return "";
};

export default function WhatWeOrchestrate() {
  const [modules, setModules] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/atlas")
      .then(({ data }) => setModules(Array.isArray(data) ? data : []))
      .catch(() => setModules([]));
  }, []);

  return (
    <section data-testid="orchestrate-section" className="relative px-6 md:px-14 py-24 md:py-32 border-b border-[#2A2A2A]">
      <div className="max-w-6xl">
        <div className="text-overline mb-6">◢ Capability stack</div>
        <h2 className="font-display font-semibold tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-white leading-[1]">
          What we <span className="text-[#FF4500]">orchestrate</span>.
        </h2>
        <p className="mt-5 text-[#8E8E93] max-w-2xl text-[15px] leading-relaxed">
          Not a list of services — a platform. Each module ships with the next one in mind.
          Tap a tile to expand.
        </p>
      </div>

      {modules.length === 0 ? (
        <div className="mt-14 ww-card p-10 max-w-3xl">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93]">◢ Loading capability stack…</div>
        </div>
      ) : (
        <div className="mt-14 grid grid-cols-1 md:grid-cols-4 auto-rows-[180px] gap-px bg-[#2A2A2A] border border-[#2A2A2A]" data-testid="orchestrate-grid">
          {modules.map((m, i) => {
            const Icon = ICONS[m.icon] || Stack;
            const isActive = active === m.id;
            const span = m.span || SPAN_FALLBACK_FOR_INDEX(i);
            // If the editor hasn't filled in short/detail yet, synthesise from bullets.
            const short = m.short || (m.bullets && m.bullets[0]) || "";
            const detail = m.detail || ((m.bullets || []).slice(1).join(" · "));

            return (
              <button
                key={m.id}
                onClick={() => setActive(isActive ? null : m.id)}
                data-testid={`orch-tile-${m.id}`}
                className={`relative bg-[#050505] hover:bg-[#0a0a0a] p-6 text-left flex flex-col justify-between group transition-colors ww-trace ${span}`}
              >
                <div className="flex items-start justify-between">
                  <Icon size={26} color={m.color || "#FF4500"} weight="duotone" />
                  <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#8E8E93]">/{String(i + 1).padStart(2, "0")}</span>
                </div>
                <div>
                  <div className="font-display text-xl text-white">{m.title}</div>
                  <p className="mt-2 text-xs text-[#8E8E93] line-clamp-2">{short}</p>
                </div>

                <AnimatePresence>
                  {isActive && detail && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.18 }}
                      className="absolute inset-0 bg-black/90 backdrop-blur-sm p-6 flex flex-col justify-end border-t-2"
                      style={{ borderTopColor: m.color || "#FF4500" }}
                    >
                      <div className="font-display text-xl text-white">{m.title}</div>
                      <p className="mt-3 text-sm text-[#F5F5F5] leading-relaxed">{detail}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
